import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import ActivityDetailDialog from '@/components/ActivityDetailDialog';
import { getActivityPill, getResultPill, getCallResultPill, getActionMeta, formatShortDate } from '@/lib/activity';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TYPE_BUTTONS = [
  { value: 'all', label: 'Tout', emoji: '' },
  { value: 'ai_call', label: 'Appel', emoji: '📞' },
  { value: 'email', label: 'Email', emoji: '✉️' },
  { value: 'sms', label: 'SMS', emoji: '💬' },
];

/**
 * Journal d'activité — une ligne par action (appel / email / SMS).
 * Partagé par la page d'accueil et l'écran Monitoring, pour qu'il n'existe
 * qu'une seule version de ce tableau.
 */
const ActivityJournal = ({ defaultPeriod = '7d' }) => {
  const [journal, setJournal] = useState({ summary: null, entries: [] });
  const [journalFilters, setJournalFilters] = useState({
    action_type: 'all', status: 'all', search: '', period: defaultPeriod,
  });
  const [journalSort, setJournalSort] = useState({ col: 'date', dir: 'desc' });
  const [selectedEntry, setSelectedEntry] = useState(null);

  const fetchJournal = useCallback(async () => {
    try {
      const params = { period: journalFilters.period };
      if (journalFilters.action_type !== 'all') params.action_type = journalFilters.action_type;
      if (journalFilters.status !== 'all') params.status = journalFilters.status;
      if (journalFilters.search.trim()) params.search = journalFilters.search.trim();
      const res = await axios.get(`${API}/monitoring/journal`, { params });
      setJournal(res.data);
    } catch (error) {
      console.error('Error fetching journal:', error);
    }
  }, [journalFilters]);

  useEffect(() => { fetchJournal(); }, [fetchJournal]);

  const toggleSort = (col) => {
    setJournalSort((prev) =>
      prev.col === col
        ? { col, dir: prev.dir === 'desc' ? 'asc' : 'desc' }
        : { col, dir: 'desc' }
    );
  };

  const sortedEntries = React.useMemo(() => {
    const rows = [...(journal.entries || [])];
    const { col, dir } = journalSort;
    const mult = dir === 'desc' ? -1 : 1;
    rows.sort((a, b) => {
      const av = col === 'date' ? (a.date || '') : (a.bucket || '');
      const bv = col === 'date' ? (b.date || '') : (b.bucket || '');
      if (av < bv) return -1 * mult;
      if (av > bv) return 1 * mult;
      return 0;
    });
    return rows;
  }, [journal.entries, journalSort]);

  return (
    <div className="space-y-4">
      {/* Metric cards — journal scope (period + type + search) */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-muted-foreground">Total actions</p>
            <p className="text-2xl font-bold">{journal.summary?.total || 0}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-muted-foreground">Réussies</p>
            <p className="text-2xl font-bold text-green-500">{journal.summary?.success_count || 0}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-muted-foreground">En file / en cours</p>
            <p className="text-2xl font-bold text-blue-500">{journal.summary?.queued_count || 0}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-muted-foreground">Échecs</p>
            <p className="text-2xl font-bold text-red-500">{journal.summary?.failed_count || 0}</p>
          </CardContent>
        </Card>
      </div>

      {/* Filter bar */}
      <div className="flex flex-col lg:flex-row lg:items-center gap-3">
        <div className="flex gap-1 flex-wrap">
          {TYPE_BUTTONS.map((t) => (
            <Button
              key={t.value}
              size="sm"
              variant={journalFilters.action_type === t.value ? 'default' : 'outline'}
              onClick={() => setJournalFilters((p) => ({ ...p, action_type: t.value }))}
              className="rounded-full"
              data-testid={`journal-type-${t.value}`}
            >
              {t.emoji && <span className="mr-1">{t.emoji}</span>}{t.label}
            </Button>
          ))}
        </div>
        <Input
          placeholder="Rechercher un client…"
          value={journalFilters.search}
          onChange={(e) => setJournalFilters((p) => ({ ...p, search: e.target.value }))}
          className="lg:max-w-xs"
          data-testid="journal-search"
        />
        <Select
          value={journalFilters.period}
          onValueChange={(v) => setJournalFilters((p) => ({ ...p, period: v }))}
        >
          <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="today">Aujourd'hui</SelectItem>
            <SelectItem value="7d">7 jours</SelectItem>
            <SelectItem value="30d">30 jours</SelectItem>
          </SelectContent>
        </Select>
        <Select
          value={journalFilters.status}
          onValueChange={(v) => setJournalFilters((p) => ({ ...p, status: v }))}
        >
          <SelectTrigger className="w-36"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tout</SelectItem>
            <SelectItem value="success">Réussi</SelectItem>
            <SelectItem value="failed">Échec</SelectItem>
            <SelectItem value="queued">En file</SelectItem>
            <SelectItem value="skipped">Ignoré</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Table — one action per row */}
      <Card>
        <CardContent className="p-0">
          {sortedEntries.length === 0 ? (
            <p className="text-muted-foreground text-center py-12">
              Aucune action pour ces filtres.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead
                      className="cursor-pointer select-none"
                      onClick={() => toggleSort('status')}
                      data-testid="journal-sort-status"
                    >
                      Statut {journalSort.col === 'status' ? (journalSort.dir === 'desc' ? '↓' : '↑') : ''}
                    </TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Client</TableHead>
                    <TableHead className="hidden sm:table-cell">Automation</TableHead>
                    <TableHead className="hidden sm:table-cell">Résultat</TableHead>
                    <TableHead
                      className="cursor-pointer select-none"
                      onClick={() => toggleSort('date')}
                      data-testid="journal-sort-date"
                    >
                      Date {journalSort.col === 'date' ? (journalSort.dir === 'desc' ? '↓' : '↑') : ''}
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sortedEntries.map((entry) => {
                    const pill = getActivityPill(entry.display_status, entry.action_type);
                    const meta = getActionMeta(entry.action_type);
                    return (
                      <TableRow
                        key={entry.id}
                        className="cursor-pointer"
                        onClick={() => setSelectedEntry(entry)}
                        data-testid="journal-row"
                      >
                        <TableCell>
                          <span
                            className={`inline-flex items-center gap-1.5 text-sm font-medium ${pill.text}`}
                            title={entry.error_message || ''}
                          >
                            <span className={`h-2 w-2 rounded-full ${pill.dot}`} />
                            {pill.label}
                          </span>
                        </TableCell>
                        <TableCell>
                          <span className="text-sm">{meta.emoji} <span className="hidden sm:inline">{meta.label}</span></span>
                        </TableCell>
                        <TableCell className="text-sm">
                          {entry.client_name ? (
                            <div>
                              <div className="font-medium text-foreground">{entry.client_name}</div>
                              {entry.client_email && (
                                <div className="text-xs text-muted-foreground">{entry.client_email}</div>
                              )}
                            </div>
                          ) : (
                            entry.client_email || entry.client_id || '—'
                          )}
                        </TableCell>
                        <TableCell className="hidden sm:table-cell text-sm text-muted-foreground">
                          {entry.automation_name || '—'}
                        </TableCell>
                        <TableCell className="hidden sm:table-cell">
                          {(() => {
                            // Pour un appel : résultat de l'appel ; sinon : engagement email.
                            const rp = entry.action_type === 'ai_call'
                              ? getCallResultPill(entry.call_detail?.reaction)
                              : getResultPill(entry.result);
                            return rp ? (
                              <span className={`inline-flex items-center gap-1.5 text-sm font-medium ${rp.text}`}>
                                <span className={`h-2 w-2 rounded-full ${rp.dot}`} />
                                {rp.label}
                              </span>
                            ) : (
                              <span className="text-sm text-muted-foreground">—</span>
                            );
                          })()}
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
                          {formatShortDate(entry.date)}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <ActivityDetailDialog entry={selectedEntry} onClose={() => setSelectedEntry(null)} />
    </div>
  );
};

export default ActivityJournal;
